import { usePricePolling } from '../hooks/usePricePolling';
import { Clock, CheckCircle, AlertTriangle, RefreshCw } from 'lucide-react';
import type { ConnectionStatus as ConnectionStatusType } from '../services/positionPersistence';

// Price Polling status component (replaces WebSocketConnectionStatus)
export function PricePollingStatus() {
  const { isPolling, lastPollTime, error } = usePricePolling();

  const minutesSinceLastPoll = lastPollTime
    ? Math.floor((Date.now() - lastPollTime) / (1000 * 60))
    : 0;
  const isDataFresh = lastPollTime > 0 && (Date.now() - lastPollTime) < 10 * 60 * 1000;

  if (isPolling) {
    return (
      <div className="fixed top-4 right-4 z-50 flex items-center space-x-2 bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-200 px-3 py-2 rounded-lg shadow-sm">
        <RefreshCw className="w-4 h-4 animate-spin" />
        <span className="text-sm font-medium">Polling prices...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed top-4 right-4 z-50 flex items-center space-x-2 bg-danger-100 dark:bg-danger-900/20 text-danger-800 dark:text-danger-200 px-3 py-2 rounded-lg shadow-sm">
        <AlertTriangle className="w-4 h-4" />
        <span className="text-sm font-medium">
          {error}
        </span>
      </div>
    );
  }

  if (isDataFresh) {
    return (
      <div className="fixed top-4 right-4 z-50 flex items-center space-x-2 bg-success-100 dark:bg-success-900/20 text-success-800 dark:text-success-200 px-3 py-2 rounded-lg shadow-sm">
        <CheckCircle className="w-4 h-4" />
        <span className="text-sm font-medium">Prices updated {minutesSinceLastPoll}m ago</span>
      </div>
    );
  }

  return (
    <div className="fixed top-4 right-4 z-50 flex items-center space-x-2 bg-warning-100 dark:bg-warning-900/20 text-warning-800 dark:text-warning-200 px-3 py-2 rounded-lg shadow-sm">
      <Clock className="w-4 h-4" />
      <span className="text-sm font-medium">
        Waiting for price update...
      </span>
    </div>
  );
}

// API connection status component (unchanged - used for position data)
interface APIConnectionStatusProps {
  connectionStatus: ConnectionStatusType;
  isUsingCache?: boolean;
  className?: string;
}

export function APIConnectionStatus({ connectionStatus, isUsingCache, className = '' }: APIConnectionStatusProps) {
  const { isConnected, lastSuccessfulFetch, consecutiveFailures, lastError } = connectionStatus;

  // Calculate time since last successful fetch
  const timeSinceLastFetch = lastSuccessfulFetch ? Date.now() - lastSuccessfulFetch : 0;
  const minutesSinceLastFetch = Math.floor(timeSinceLastFetch / (1000 * 60));

  // Determine status and styling
  const getStatusInfo = () => {
    if (isConnected) {
      return {
        icon: CheckCircle,
        color: 'text-green-600',
        bgColor: 'bg-green-50',
        borderColor: 'border-green-200',
        status: 'API Connected',
        message: 'Position data is live'
      };
    }

    if (isUsingCache) {
      return {
        icon: Clock,
        color: 'text-yellow-600',
        bgColor: 'bg-yellow-50',
        borderColor: 'border-yellow-200',
        status: 'Using Cache',
        message: `Showing cached positions (${minutesSinceLastFetch}m ago)`
      };
    }

    return {
      icon: AlertTriangle,
      color: 'text-red-600',
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200',
      status: 'API Issues',
      message: lastError?.message || 'Unable to fetch positions'
    };
  };

  const statusInfo = getStatusInfo();
  const StatusIcon = statusInfo.icon;

  return (
    <div className={`flex items-center space-x-2 px-3 py-2 rounded-lg border ${statusInfo.bgColor} ${statusInfo.borderColor} ${className}`}>
      <StatusIcon className={`h-4 w-4 ${statusInfo.color}`} />
      <div className="flex-1 min-w-0">
        <div className={`text-sm font-medium ${statusInfo.color}`}>
          {statusInfo.status}
        </div>
        <div className="text-xs text-gray-600 truncate">
          {statusInfo.message}
        </div>
      </div>

      {consecutiveFailures > 0 && (
        <div className="flex items-center space-x-1">
          <AlertTriangle className="h-3 w-3 text-orange-500" />
          <span className="text-xs text-orange-600 font-medium">
            {consecutiveFailures} failures
          </span>
        </div>
      )}
    </div>
  );
}

// Badge version for compact display
interface ConnectionStatusBadgeProps {
  connectionStatus: ConnectionStatusType;
  isUsingCache?: boolean;
}

export function ConnectionStatusBadge({ connectionStatus, isUsingCache }: ConnectionStatusBadgeProps) {
  const { isConnected } = connectionStatus;

  if (isConnected && !isUsingCache) {
    return (
      <div className="flex items-center space-x-1">
        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
        <span className="text-xs text-green-600 font-medium">Live</span>
      </div>
    );
  }

  if (isUsingCache) {
    return (
      <div className="flex items-center space-x-1">
        <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
        <span className="text-xs text-yellow-600 font-medium">Cached</span>
      </div>
    );
  }

  return (
    <div className="flex items-center space-x-1">
      <div className="w-2 h-2 bg-red-500 rounded-full"></div>
      <span className="text-xs text-red-600 font-medium">Offline</span>
    </div>
  );
}

// Keep the original export for backward compatibility (now uses polling status)
export const ConnectionStatus = PricePollingStatus;

// Legacy alias for WebSocketConnectionStatus
export const WebSocketConnectionStatus = PricePollingStatus;
